import { type PropsWithChildren, createElement, memo, useMemo } from "react";

import he from "he";

import {
  type WithBreakpoints,
  getSizeWithBreakpoints,
} from "@react/utilities/responsive";
import { type TTailwindString, tw } from "classnames";

export type TextType =
  | "body0"
  | "body1"
  | "body2"
  | "title1"
  | "title2"
  | "button"
  | "caption";
type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type Align = "left" | "center" | "right";
type Leading = "none" | "tight" | "snug" | "normal" | "relaxed" | "loose";
type SizeValue = "3xl" | "lg" | "md" | "sm" | "xs" | "2xs" | "3xs";

export type Props = {
  align?: Align;
  as?: TextType;
  className?: TTailwindString;
  headingLevel?: HeadingLevel;
  id?: string;
  leading?: Leading;
  size?: SizeValue | WithBreakpoints<SizeValue>;
};

const defaultSizes = {
  body0: { sm: "lg", md: "2xl" },
  body1: { sm: "md", md: "lg" },
  body2: { sm: "sm", md: "md" },
  title1: { sm: "lg" },
  title2: { sm: "xs" },
  button: { sm: "md" },
  caption: { sm: "xs" },
} as const;

const TextBase = ({
  align,
  as = "body2",
  children,
  className,
  headingLevel,
  id,
  leading,
  size: originalSize,
}: PropsWithChildren<Props>) => {
  const size = useMemo(() => {
    const resolvedSize =
      typeof originalSize === "string"
        ? { sm: originalSize, md: originalSize }
        : { ...defaultSizes[as], ...originalSize };

    return getSizeWithBreakpoints(resolvedSize);
  }, [as, originalSize]);

  let resolvedLeading;
  let weight = null;
  let tracking = null;
  let transform = null;

  switch (as) {
    case "body0":
      resolvedLeading = leading ?? "snug";
      break;
    case "body1":
      resolvedLeading = leading ?? "snug";
      break;
    case "body2":
      resolvedLeading = leading ?? "snug";
      break;
    case "title1":
      resolvedLeading = leading ?? "none";
      weight = "semibold";
      tracking = "widest";
      transform = "uppercase";
      break;
    case "title2":
      resolvedLeading = leading ?? "none";
      weight = "semibold";
      tracking = "widest";
      transform = "uppercase";
      break;
    case "button":
      resolvedLeading = leading ?? null;
      weight = "semibold";
      tracking = "wider";
      transform = "uppercase";
      break;
    case "caption":
      resolvedLeading = leading ?? "snug";
      break;
  }

  const resolvedClassName = tw(
    "font-calibre",
    "antialiased",
    "m-0",
    "p-0",
    {
      "text-3xl": size.sm === "3xl",
      "text-lg": size.sm === "lg",
      "text-base": size.sm === "md",
      "text-sm": size.sm === "sm",
      "text-xs": size.sm === "xs",
      "text-2xs": size.sm === "2xs",
      "text-3xs": size.sm === "3xs",
    },
    {
      "md:text-3xl": size.md === "3xl",
      "md:text-2xl": size.md === "2xl",
      "md:text-lg": size.md === "lg",
      "md:text-base": size.md === "md",
      "md:text-sm": size.md === "sm",
      "md:text-xs": size.md === "xs",
      "md:text-2xs": size.md === "2xs",
      "md:text-3xs": size.md === "3xs",
    },
    {
      "mg:text-3xl": size.mg === "3xl",
      "mg:text-lg": size.mg === "lg",
      "mg:text-base": size.mg === "md",
      "mg:text-sm": size.mg === "sm",
      "mg:text-xs": size.mg === "xs",
      "mg:text-2xs": size.mg === "2xs",
      "mg:text-3xs": size.mg === "3xs",
    },
    {
      "lg:text-3xl": size.lg === "3xl",
      "lg:text-lg": size.lg === "lg",
      "lg:text-base": size.lg === "md",
      "lg:text-sm": size.lg === "sm",
      "lg:text-xs": size.lg === "xs",
      "lg:text-2xs": size.lg === "2xs",
      "lg:text-3xs": size.lg === "3xs",
    },
    {
      "text-left": align === "left",
      "text-center": align === "center",
      "text-right": align === "right",
    },
    {
      "leading-none": resolvedLeading === "none",
      "leading-tight": resolvedLeading === "tight",
      "leading-snug": resolvedLeading === "snug",
      "leading-normal": resolvedLeading === "normal",
      "leading-relaxed": resolvedLeading === "relaxed",
      "leading-loose": resolvedLeading === "loose",
    },
    {
      uppercase: transform === "uppercase",
    },
    {
      "font-normal": weight !== "semibold",
      "font-semibold": weight === "semibold",
    },
    {
      "tracking-normal": tracking !== "wider" && tracking !== "widest",
      "tracking-wider": tracking === "wider",
      "tracking-widest": tracking === "widest",
    },
    className ? tw(className) : undefined
  );

  const props = headingLevel
    ? {
        className: resolvedClassName,
        role: "heading",
        "aria-level": headingLevel,
        ...(id && { id }),
      }
    : {
        className: resolvedClassName,
        ...(id && { id }),
      };

  const content = typeof children === "string" ? he.decode(children) : children;
  return createElement("p", { ...props }, content);
};

export const Text = memo(TextBase);
